import { IExportColumn, IExportRequest, IExportSection } from '../interfaces';
import { normalizeExportValue, resolveExportCellValue } from './export-row.utils';

/**
 * One row of a rendered section: a header line plus the body lines beneath it.
 */
export interface IExportSectionTable {
  /** Section heading, already resolved to a usable string. */
  title: string;
  /** Header cells of the rendered table. */
  head: string[];
  /** Body cells of the rendered table, one array per output row. */
  body: string[][];
}

/**
 * Normalises either request shape into the section list the PDF strategy works
 * on.
 *
 * @param request - The export request.
 * @returns One section per part of the document, never empty.
 * @throws When the request carries neither sections nor rows/columns.
 *
 * @remarks
 * PDF-only. CSV and Excel take exactly one table per file and read
 * `rows` / `columns` directly, so they deliberately do not use this — their
 * cells must keep their original types, and everything below stringifies.
 *
 * Metadata is left as `unknown`: nothing here reads it, and pinning it would
 * stop a strategy that has narrowed its own metadata from passing its request
 * straight through.
 */
export function toExportSections<TRow>(request: IExportRequest<TRow, unknown>): IExportSection<TRow>[] {

  // Multi-section form wins when both are present.
  if (request.sections != null && request.sections.length > 0) {
    return request.sections;
  }

  if (request.columns == null) {
    throw new Error('Export request must carry either "sections" or "columns".');
  }

  return [
    {
      title: request.title,
      rows: request.rows ?? [],
      columns: request.columns,
      layout: 'rows',
    },
  ];
}

/**
 * Renders one section into header/body string cells, applying its layout.
 *
 * @param section - The section to render.
 * @param fallbackTitle - Title used when the section carries none.
 * @param transform - Optional per-cell post-processing, applied to every
 *   header and body cell after value resolution. The PDF strategy passes its
 *   bidi pass through here so no cell can be missed.
 * @returns The rendered table.
 */
export function renderExportSection<TRow>(
  section: IExportSection<TRow>,
  fallbackTitle: string,
  transform?: (value: string) => string,
): IExportSectionTable {

  const apply = transform ?? ((value: string) => value);
  const title = apply(section.title?.trim() || fallbackTitle);

  // Transposed: one output row per column, so a wide record reads down a page
  // instead of across four of them.
  if (section.layout === 'fields') {
    return {
      title,
      head: [],
      body: section.columns.map((column) => [
        apply(column.header),
        ...section.rows.map((row) => apply(readCell(row, column))),
      ]),
    };
  }

  return {
    title,
    head: section.columns.map((column) => apply(column.header)),
    body: section.rows.map((row) => section.columns.map((column) => apply(readCell(row, column)))),
  };
}

/**
 * Splits a rendered table into bands, each as wide as the page allows.
 *
 * @param table - The rendered table.
 * @param widths - Natural width of each column, in the same unit as
 *   `usableWidth`. Measured by the caller, which is the only place that knows
 *   the document's font metrics.
 * @param usableWidth - Width one band may occupy.
 * @returns One table per band, in order.
 *
 * @remarks
 * Greedy rather than a fixed column count: bands come out uneven — two narrow
 * columns here, one long meter label alone there — which is exactly what makes
 * a wrapped record readable, and what a single count for the whole table cannot
 * produce. A column wider than the page still gets its own band rather than
 * being dropped, so every column reaches exactly one band.
 */
export function chunkExportTableToWidth(
  table: IExportSectionTable,
  widths: readonly number[],
  usableWidth: number,
): IExportSectionTable[] {

  const bands: IExportSectionTable[] = [];

  let start = 0;

  while (start < table.head.length) {

    let end = start;
    let consumed = 0;

    // `end === start` guarantees progress: the first column of a band is taken
    // regardless of its width, so an over-wide column cannot loop forever.
    while (end < table.head.length && (end === start || consumed + (widths[end] ?? 0) <= usableWidth)) {
      consumed += widths[end] ?? 0;
      end += 1;
    }

    bands.push({
      title: table.title,
      head: table.head.slice(start, end),
      body: table.body.map((row) => row.slice(start, end)),
    });

    start = end;
  }

  return bands;
}

/**
 * Splits a rendered table into the bands the caller declared.
 *
 * @param table - The rendered table.
 * @param sizes - Column count of each band, in order.
 * @returns One table per band, in order.
 *
 * @remarks
 * The counterpart to {@link chunkExportTableToWidth}: the same bands, decided
 * by the caller rather than by the page width. A report whose fields are
 * grouped by meaning — the unit and its bill number on one line, the meter
 * alone on the next — needs this, because a width fit groups whatever happens
 * to fit and reads as an arbitrary shuffle.
 *
 * Two guards keep every column reaching exactly one band: a non-positive count
 * is skipped rather than looping forever, and columns left over after the last
 * count form one final band, so a stale count list loses nothing.
 */
export function chunkExportTableToBands(table: IExportSectionTable, sizes: readonly number[]): IExportSectionTable[] {

  const total = table.head.length;
  const bands: IExportSectionTable[] = [];

  const push = (start: number, end: number): void => {
    bands.push({
      title: table.title,
      head: table.head.slice(start, end),
      body: table.body.map((row) => row.slice(start, end)),
    });
  };

  let start = 0;

  for (const size of sizes) {

    if (start >= total) {
      break;
    }

    const width = Math.floor(size);

    if (width <= 0) {
      continue;
    }

    const end = Math.min(start + width, total);

    push(start, end);

    start = end;
  }

  if (start < total) {
    push(start, total);
  }

  return bands.length > 0 ? bands : [table];
}

/**
 * Resolves one cell to its export string.
 *
 * @param row - The source row.
 * @param column - The column definition.
 * @returns The normalized cell value as a string.
 */
function readCell<TRow>(row: TRow, column: IExportColumn<TRow>): string {
  return String(normalizeExportValue(resolveExportCellValue(row, column)));
}
